<template>
  <hr-page>
    <breadcrumbs hide-client />
    <records-table
      v-model:pagination="pagination"
      :rows="clientProfiles"
      :columns="clientsColumns"
      :column-group-labels="clientsColumnGroups"
      :default-columns="['clientName', 'units', 'status']"
      :default-filters="defaultFilter"
      :loading="isLoadingClients || isLoadingUsers"
      :title="$t('clients.breadcrumbElLabel')"
      :default-view-label="$t('clients.defaultViewLabel')"
      :no-data-label="$t('clients.tableNoDataLabel')"
      :search-placeholder="$t('clients.inputSearch')"
      :create="createAction"
      standalone
      save-filter-id="clients"
      export-filename="clients"
      @row-click.stop="onEditClientRow"
    >
      <template #body-cell-clientName="props">
        <q-td
          :props="props"
          :data-e2e="'row-client-' + props.row.clientName"
        >
          <span
            class="cursor-pointer"
          >
            <q-avatar
              size="24px"
              class="q-mr-sm"
            >
              <q-img :src="getClientProfileImagePath(props.row)" />
            </q-avatar> {{ props.row.clientName }}
          </span>
        </q-td>
      </template>
      <template #body-cell-integration="props">
        <q-td :props="props">
          <q-chip
            v-if="props.value"
            :label="$t('settings.users.labelIntegrationConnected')"
            icon="check"
            color="primary"
            size="12px"
            text-color="white"
          />
        </q-td>
      </template>
      <!-- One overflow menu and nothing beside it: every row action lives in the menu already. -->
      <template #row-actions="{ row }">
        <hr-icon-menu-btn
          icon="more_vert"
          size="sm"
          :aria-label="$t('clients.labelClientActions', { clientName: row.clientName })"
          data-e2e="button-client-actions"
          @click.stop="openRowMenuId = openRowMenuId === row.id ? null : row.id"
        >
          <hr-adaptive-menu
            :model-value="openRowMenuId === row.id"
            @update:model-value="openRowMenuId = $event ? row.id : null"
          >
            <!--
              Three groups: what to do with this client, what to look up about them, and what
              changes their standing. Editing leads because it is the primary action, which is also
              why the row click does it — the menu states the primary action, it does not avoid
              repeating it.
            -->
            <hr-menu-item
              v-close-popup
              icon="edit"
              :label="$t('clients.listItemTextEdit')"
              :disable="offlineMode"
              data-e2e="list-item-edit-client"
              @click="onEditClient(row.id)"
            />
            <hr-menu-item
              v-close-popup
              icon="home"
              :label="$t('clients.listItemTextSelectClient')"
              :disable="offlineMode"
              data-e2e="list-item-go-to-client-home"
              @click="onGoToClient(row)"
            />
            <hr-menu-item
              v-if="canManageUsers"
              v-close-popup
              icon="group"
              :label="$t('clients.listItemTextManageUsers')"
              :disable="offlineMode"
              data-e2e="list-item-manage-users"
              @click="onManageUsers(row.id)"
            />

            <hr-divider />
            <hr-menu-item
              v-if="showRefreshAppointments(row)"
              v-close-popup
              icon="refresh"
              :label="$t('clients.listItemTextRefreshAppointments', {
                integration: getIntegrationName(connectedClinic.integration)
              })"
              :trailing-text="isAppointmentsRefreshing
                ? $t('clients.listItemTooltipRefreshInProgress', {
                  integration: getIntegrationName(connectedClinic.integration)
                })
                : null"
              :disable="offlineMode || isAppointmentsRefreshing"
              @click="onRefreshAppointments(row)"
            />
            <!--
              The upgrade hint rides the item as trailing text rather than the tooltip it used to
              have: a tooltip on a disabled row only appears on hover, and this is the one thing
              that explains why the row is disabled.
            -->
            <hr-menu-item
              v-if="userCanViewAuditTrail"
              v-close-popup
              icon="fact_check"
              :label="$t('auditLogReports.viewAuditTrail')"
              :trailing-text="disableAuditTrail ? $t('premiumUpgrade.upgradeHint') : null"
              :disable="disableAuditTrail"
              data-e2e="list-item-view-audit-trail"
              @click="onViewAuditTrail(row)"
            />
            <hr-menu-item
              v-close-popup
              icon="content_copy"
              :label="$t('common.copySupportInfo')"
              data-e2e="list-item-copy-support-info"
              @click="onCopySupportInfo(row.id)"
            />

            <!-- Last group, and the only one whose entries end the client's standing here. -->
            <hr-divider />
            <hr-menu-item
              v-if="row.status === 'active'"
              v-close-popup
              icon="person_off"
              :label="$t('clients.listItemTextDisable')"
              :disable="isStatusSwitchDisabled(row)"
              data-e2e="dropdown-option-inactive-client"
              @click="onDisableClient(row.id, row.clientName)"
            />
            <hr-menu-item
              v-if="hasSupervisorCredentials && !isAdministrator"
              v-close-popup
              icon="delete"
              destructive
              :label="$t('clients.listItemTextDelete')"
              :disable="offlineMode"
              data-e2e="list-item-delete-client"
              @click="onDeleteClient(row)"
            />
          </hr-adaptive-menu>
        </hr-icon-menu-btn>
      </template>
      <!--
        Two states, because they are two problems. Nothing here yet is answered by filling the
        collection, and the import routes are offered beside the primary rather than folded into
        its menu: this is the one screen where a reader has no records and most needs to know that
        bringing a list in is possible.
      -->
      <template #empty>
        <hr-empty-state
          icon="group"
          :title="$t('clients.emptyTitle')"
          :message="createAction ? $t('clients.emptyMessage') : null"
        >
          <template
            v-if="createAction"
            #actions
          >
            <hr-page-action-btn
              :label="createAction.label"
              :disable="createAction.disable"
              :data-e2e="createAction.dataE2e"
              @click="createAction.handler"
            />
            <hr-supporting-btn
              v-for="path in createAction.menu"
              :key="path.key"
              :label="path.label"
              :icon="path.icon"
              :data-e2e="path.dataE2e"
              @click="path.handler"
            />
          </template>
        </hr-empty-state>
      </template>

      <!-- Records exist and none are showing, so the way out is to stop hiding them. -->
      <template #empty-filtered="{ hasSearchTerm, hasFilters, clearSearch, clearFilters }">
        <hr-empty-state
          icon="search_off"
          :title="$t('clients.emptyFilteredTitle')"
          :message="$t('clients.emptyFilteredMessage')"
        >
          <template #actions>
            <hr-supporting-btn
              v-if="hasSearchTerm"
              :label="$t('common.buttonClearSearch')"
              data-e2e="button-clear-search"
              @click="clearSearch"
            />
            <hr-subtle-btn
              v-if="hasFilters"
              :label="$t('common.buttonClearFilters')"
              data-e2e="button-clear-filters"
              @click="clearFilters"
            />
          </template>
        </hr-empty-state>
      </template>
    </records-table>
  </hr-page>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import { filterPropertyTypes } from 'boot/globals';
import ViewAuditTrailDialog from 'components/Audit/ViewAuditTrailDialog';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import DeleteDisableClient from 'components/Clients/Modals/DeleteDisableClient';
import RestoreClientsDialog from 'components/Clients/Modals/RestoreClientsDialog.vue';
import ImportClientsDialog from 'components/Integrations/ImportClientsDialog';
import RecordsTable from 'components/UI/RecordsTable.vue';
import {
  chain, get, map, reject, size,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import {
  HrAdaptiveMenu,
  HrDivider,
  HrEmptyState,
  HrIconMenuBtn,
  HrMenuItem,
  HrPage,
  HrPageActionBtn,
  HrSubtleBtn,
  HrSupportingBtn,
} from 'src/design-system';
import { useClients } from 'src/composables/clients';
import { useEditClientProfile } from 'src/composables/editClientProfile';
import { useTriggerNotifications } from 'src/composables/triggerNotifications';
import { EDIT_CLIENT_PROFILE_SOURCES } from 'src/constants/analytics';
import { CLIENT_RECOVERY_WINDOW_DAYS, CLIENT_STATUS, EDIT_CLIENT_PROFILE_TABS } from 'src/constants/clients';
import { POLLING_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import { KNOWLEDGE_BASE_ARTICLES } from 'src/constants/knowledgeBase';
import { CLIENTS_NOTIFICATIONS_TYPES } from 'src/constants/notifications';
import { ImportDialog, IMPORT_TYPES } from 'src/contexts/onboarding';
import SessionsDB from 'src/fb/sessions-db';
import Auth from 'src/mixins/Auth';
import ClientsMixin from 'src/mixins/ClientsMixin';
import Countries from 'src/mixins/Countries';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import LicenseProducts from 'src/mixins/LicenseProducts';
import Toolbox from 'src/mixins/Toolbox';
import { clientLifecycleAnalytics } from 'src/services/analytics';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { network } from 'src/store/network';
import { units } from 'src/store/units';
import { users } from 'src/store/users';
import AppUtils, { copySupportInfoToClipboard, notifyErrorWithDetails } from 'src/utils/AppUtils';
import { sendEventToAloha, updateAppointmentClientReference } from 'src/utils/Integrations';
import { logEvent } from 'src/utils/LogEvent';

export default {

  name: 'Clients',

  components: {
    Breadcrumbs,
    HrAdaptiveMenu,
    HrDivider,
    HrEmptyState,
    HrIconMenuBtn,
    HrMenuItem,
    HrPage,
    HrPageActionBtn,
    HrSubtleBtn,
    HrSupportingBtn,
    RecordsTable,
  },

  mixins: [
    Auth,
    ClientsMixin,
    Integrations,
    Interactions,
    LicenseProducts,
    Countries,
    Toolbox,
  ],

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      await vm.initializeClients();
      await vm.syncClients();
      await Promise.all([
        vm.loadClinicUnits(),
        vm.loadCachedUserPrivileges(),
      ]);

      // Bind users only if user has privilege to manage users
      if (vm.canManageUsers) {
        vm.loadUsers();
      }
    });
  },

  setup() {
    const { createClientsTriggerNotifications } = useTriggerNotifications();
    const {
      getClientProfileImagePath, getDaysSinceDeleted, getRandomColor, isRestorable,
    } = useClients();
    const {
      canManageUsers,
      canAddClients,
      loadCachedUserPrivileges,
      openClientProfile,
      openNewClientProfile,
    } = useEditClientProfile();

    return {
      createClientsTriggerNotifications,
      getClientProfileImagePath,
      getRandomColor,
      canManageUsers,
      canAddClients,
      loadCachedUserPrivileges,
      openClientProfile,
      openNewClientProfile,
      getDaysSinceDeleted,
      isRestorable,
    };
  },

  data() {
    return {
      isLoadingUsers: false,

      pagination: {
        sortBy: 'clientName',
        descending: false,
        page: 1,
        rowsPerPage: 50,
      },

      isAppointmentsRefreshing: false,

      // One row's overflow menu at a time; a shared boolean would open every row's menu at once.
      openRowMenuId: null,

      usersLoadingPromise: null,
    };
  },

  computed: {
    ...mapState(auth, ['userSettings', 'userDetails', 'currentClient', 'connectedClinic', 'billing',
      'isManager', 'isAdministrator', 'isManagerOrAdministrator',
      'hasSupervisorCredentials', 'isSubscriptionPremium', 'isSubscriptionPastDue', 'autoImportClients',
      'getAvailableLicenses']),
    ...mapState(clients, ['isLoadingClients', 'clients']),
    ...mapState(units, ['units', 'userUnits']),
    ...mapState(users, ['users']),
    ...mapState(network, ['offlineMode', 'networkOnline']),

    // Read by ClientsMixin's status column, which has no default of its own (see its @todo). This
    // page lost its Staff tab and is only ever the client one, so it is a constant rather than the
    // tab model it used to be. Removing it relabelled Status as "License Type" and rendered
    // Active/Inactive as Premium/Standard, because the mixin's staff branch wins on undefined.
    personType() {
      return 'clients';
    }, // personType()

    /*
     * Adding, importing a file and pulling from the integration all end with more clients on the
     * list, so they are three routes into one decision rather than three controls competing for the
     * header. Ranking happens inside the menu, not across the page header.
     */
    createAction() {
      if (!this.canAddClients) {
        return null;
      }

      const menu = [];
      if (this.isManager) {
        menu.push({
          key: 'import-file',
          label: this.$t('clients.buttonBulkImportClients'),
          icon: 'upload_file',
          dataE2e: 'list-item-bulk-import-clients',
          disable: this.offlineMode,
          // Picking a spreadsheet of clients off a phone is not a workflow anyone has.
          expandedOnly: true,
          handler: () => this.onImportClients(),
        });
      }
      if (!this.offlineMode && this.connectedClinic.integration) {
        menu.push({
          key: 'import-integration',
          label: this.$t('clients.importFromIntegration', {
            integration: this.getIntegrationName(this.connectedClinic.integration),
          }),
          icon: 'fas fa-file-import',
          dataE2e: 'list-item-import-from-integration',
          handler: () => this.onImportFromIntegration(),
        });
      }
      if (this.isManager && !this.connectedClinic.integration) {
        menu.push({
          key: 'restore-client',
          label: this.$t('clients.restoreClients'),
          icon: 'restore_from_trash',
          disable: this.offlineMode,
          handler: () => this.onRestoreClients(),
        });
      }

      return {
        key: 'add-client',
        label: this.$t('clients.buttonAddClient'),
        dataE2e: 'button-add-client',
        disable: this.offlineMode,
        handler: () => this.onAddClient(),
        menu,
      };
    },

    userCanViewAuditTrail() {
      return this.isManagerOrAdministrator || this.userDetails.admin;
    },

    disableAuditTrail() {
      return !this.isSubscriptionPremium;
    },

    clientsColumns() {
      return chain(this.clientColumns)
        .filter((column) => (chain(column).get('profileTypes').includes('client').value()))
        .concat(
          map(
            this.clientDataColumns,
            (clientDataColumn) => ({
              ...clientDataColumn,
              group: 'clientData',
            }),
          ),
        )
        .concat(this.lastSessionColumns)
        .value();
    }, // clientsColumns()

    clientsColumnGroups() {
      return [
        {
          group: 'clientData',
          label: this.$t('clients.columnGroupClientData'),
        },
        {
          group: 'integration',
          label: this.$t('clients.columnGroupIntegration', {
            integration: this.getIntegrationName(get(this.connectedClinic, 'integration')),
          }),
        },
        {
          group: 'lastSession',
          label: this.$t('clients.columnGroupLastSession'),
        },
      ];
    }, // clientsColumnGroups()

    clientProfiles() {
      return reject(this.clients, { type: 'staff' });
    }, // clientProfiles()

    defaultFilter() {
      return {
        status: {
          filterProperty: 'status',
          filterPropertyType: filterPropertyTypes.StringInArray,
          filterStatements: [
            {
              filterDateItems: [],
              fromDate: null,
              toDate: null,
              customDataId: null,
              filterCriteriaType: null,
              filterNegative: false,
              filteredItems: ['active'],
              label: `${this.$t('clients.columnsStatusLabel')}: ${this.$t('clients.status.active')}`,
            },
          ],
          id: null,
        },
      };
    }, // defaultFilter()

  },

  methods: {
    ...mapActions(auth, ['changeCurrentClient', 'changeCurrentClientById', 'selectDisabledClient']),
    ...mapActions(clients, [
      'initializeClients',
      'syncClients',
      'updateClient',
      'deleteClient',
      'softDeleteClient',
      'loadDeletedClients',
    ]),
    ...mapActions(units, ['loadClinicUnits']),
    ...mapActions(users, ['bindUsers']),

    async onAddClient() {
      if (!this.canAddClients) {
        return;
      }

      if (this.isSubscriptionPastDue) {
        return this.confirmDialog(
          this.$t('clients.dialogAddClientUnavailableTitle'),
          this.$t('clients.dialogAddClientUnavailableMessage'),
          () => {},
          {
            cancel: false,
          },
        );
      }

      const editedClient = {
        id: null,
        clientName: null,
        alias: null,
        address1: null,
        address2: null,
        postalCode: null,
        city: null,
        state: null,
        country: this.connectedClinic.country,
        phone: null,
        email: null,
        gender: null,
        notes: null,
        units: (size(this.userDetails.units) === 1) ? this.userDetails.units : [],
        clinic: this.userDetails.clinic,
        status: 'active',
        avatarBgColor: this.getRandomColor(),
      };

      await this.openNewClientProfile(editedClient);
    }, // onAddClient()

    onImportClients() {
      this.openComponentDialog(
        ImportDialog,
        {
          rootEntityType: IMPORT_TYPES.CLIENT,
          existingData: this.clientProfiles,
          guideUrl: KNOWLEDGE_BASE_ARTICLES.IMPORT_CLIENTS,
        },
      );
    }, // onImportClients()

    onImportFromIntegration() {
      this.showImportClientsDialog();
    },

    async onEditClient(clientId) {
      const editedClient = {
        id: clientId,
        ...this.clients.find((x) => x.id === clientId),
      };

      this.$q.loading.show();
      try {
        await this.openClientProfile(editedClient, { source: EDIT_CLIENT_PROFILE_SOURCES.CLIENTS });
      } finally {
        this.$q.loading.hide();
      }
    },

    onEditClientRow(evt, client) {
      if (!get(client, 'id')) {
        return;
      }
      this.onEditClient(get(client, 'id'));
    },

    async onRefreshAppointments(client) {
      let dialogHidden = false;
      const dialog = this.$q.dialog({
        message: this.$t('settings.integrations.importClients.refreshingText', { countRefs: 0, totalRefs: 1 }),
        progress: true,
        persistent: false,
        ok: false,
      }).onDismiss(() => {
        dialogHidden = true;
      });
      this.isAppointmentsRefreshing = true;
      try {
        await callFirebaseFunction('refreshAppointments', {
          clinicId: this.userDetails.clinic,
          objectType: 'client',
          objectId: client.integrationReference,
          ignoreModifiedSince: true,
        }, {
          timeout: 540000, // timeout aligned with firebase function timeout
        });
        this.notifySuccess(this.$t('clients.appointmentsSuccessfullyRefreshed'));
      } catch (err) {
        const isOfflineError = ['internal', 'functions/internal'].includes(err.code)
          && err.message === 'internal'
          && !this.networkOnline;
        const errorMessage = isOfflineError ? this.$t('common.noInternetConnectionMessage') : err.message;
        notifyErrorWithDetails(errorMessage, err.details?.fullMessage);
      }
      this.isAppointmentsRefreshing = false;
      if (!dialogHidden) {
        dialog.hide();
      }
    },

    async onDisableClient(clientId, clientName) {
      this.openComponentDialog(
        DeleteDisableClient,
        {
          title: this.$t('clients.dialogDisableClientTitle'),
          message: this.$t('clients.dialogDisableClientMessage', {
            clientName,
          }),
          additionalText: this.$t('clients.dialogDisableClientAddMessage'),
          bannerMessage: this.$t('clients.dialogDisableClientBannerMessage'),
          bannerLinkMessage: this.$t('clients.dialogDisableClientBannerLinkMessage'),
          okLabel: this.$t('common.buttonInactivate'),
        },
        async () => {
          // Delete UsersClients associated with client
          try {
            this.$q.loading.show();

            await this.updateClient({ status: 'inactive', id: clientId });
            this.createClientsTriggerNotifications(clientId, CLIENTS_NOTIFICATIONS_TYPES.CLIENT_UPDATED);
            await this.updateLicenseUsage();

            this.$q.loading.hide();
            this.notifySuccess(this.$t('clients.notifyDisableClientSuccess', { clientName }));
          } catch (error) {
            this.notifyNegative(error.message);
          }
        },
      );
    }, // onDisableClient()

    async onDeleteClient({ id, clientName, integrationReference, status }) {
      const deleteDialogConfig = this.connectedClinic?.integration
        ? {
          title: this.$t('clients.dialogDeleteClientTitle', { clientName }),
          message: this.$t('clients.dialogDeleteClientMessage'),
          additionalText: this.$t('clients.dialogDeleteClientAddMessage'),
          okLabel: this.$t('common.buttonDelete'),
        }
        : {
          title: this.$t('clients.dialogSoftDeleteClientTitle', { clientName }),
          message: this.$t('clients.dialogSoftDeleteClientMessage', { days: CLIENT_RECOVERY_WINDOW_DAYS }),
          additionalText: this.$t('clients.dialogSoftDeleteClientAddMessage'),
          okLabel: this.$t('clients.buttonDeleteClient'),
        };

      this.openComponentDialog(
        DeleteDisableClient,
        {
          target: clientName,
          status,
          ...deleteDialogConfig,
          bannerMessage: this.$t('clients.dialogDeleteClientBannerMessage'),
          bannerLinkMessage: this.$t('clients.dialogDisableClientBannerLinkMessage'),
        },
        async () => {
          // Delete UsersClients associated with client
          const sessionsDb = new SessionsDB();
          const isPermanent = Boolean(this.connectedClinic?.integration);
          let deleteWritten = false;

          try {
            this.$q.loading.show();
            const activeClientSessions = await sessionsDb.readAll([
              ['clinic', '==', this.userDetails.clinic],
              ['client', '==', id],
              ['status', '==', 'running'],
            ]);

            if (activeClientSessions.length) {
              clientLifecycleAnalytics.trackClientDeleteBlocked({
                targetClientId: id,
                reason: clientLifecycleAnalytics.DELETE_BLOCKED_REASONS.SESSION_IN_PROGRESS,
              });
              this.notifyWarning(this.$t('clients.notifyDeleteClientInSession'));
              return;
            }

            // Integrated clinics keep the hard delete. Elsewhere, the client is soft-deleted first:
            // deleteClientSessionActivities refuses to run until the client is tombstoned.
            if (isPermanent) {
              await callFirebaseFunction('deleteClientSessionActivities', {
                clinicId: this.userDetails.clinic,
                clientId: id,
              });

              await this.deleteClient(id);
              deleteWritten = true;
              clientLifecycleAnalytics.trackClientDeleted({ targetClientId: id, clientStatus: status, isPermanent });
            } else {
              // False when another user tombstoned the client first, so the delete isn't counted twice.
              const tombstoned = await this.softDeleteClient(id);
              deleteWritten = true;
              if (tombstoned) {
                clientLifecycleAnalytics.trackClientDeleted({ targetClientId: id, clientStatus: status, isPermanent });
              }

              // Client is already deleted, so a failure here must not skip the license update or
              // report the delete as failed. deletedClientsCleanup sweeps any activity left live.
              try {
                await callFirebaseFunction('deleteClientSessionActivities', {
                  clinicId: this.userDetails.clinic,
                  clientId: id,
                });
              } catch (err) {
                logEvent('Error deleting session activities of a soft-deleted client', {
                  context: { context_data: { clientId: id, errorCode: err?.code ?? null } },
                });
              }
            }

            await this.updateLicenseUsage();

            if (get(this.connectedClinic, 'integration') === 'aloha' && integrationReference) {
              await AppUtils.allSettled([
                updateAppointmentClientReference(this.connectedClinic.id, null, integrationReference),
                sendEventToAloha(
                  this.connectedClinic.id,
                  'PatientMappingsRemoved',
                  { refId: integrationReference },
                ),
              ]);
            }

            await this.syncClients();
            this.notifySuccess(this.$t('clients.notifyDeleteClientSuccess', { clientName }), {
              timeout: 5000,
            });
          } catch (error) {
            if (!deleteWritten) {
              clientLifecycleAnalytics.trackClientDeleteFailed({
                targetClientId: id,
                isPermanent,
                errorCode: error?.code,
              });
            }
            this.notifyNegative(error.message);
          } finally {
            this.$q.loading.hide();
          }
        },
      );
    }, // onDeleteClient()

    async onRestoreClients() {
      this.$q.loading.show();

      let deletedClients;
      try {
        deletedClients = await this.loadDeletedClients(this.userDetails.clinic);
      } catch (err) {
        this.notifyNegative(this.$t('common.errors.generic'));
        return;
      } finally {
        this.$q.loading.hide();
      }

      const restorableCount = deletedClients
        .filter(({ deleteTimestamp }) => this.isRestorable(deleteTimestamp?.toDate() ?? null))
        .length;
      clientLifecycleAnalytics.trackDeletedClientsViewed({ restorableCount });

      this.openComponentDialog(RestoreClientsDialog, {
        softDeletedClients: deletedClients,
        restoreClient: this.onRestoreDeletedClient,
      });
    }, // onRestoreClients()

    /**
     * @param {Object} client - a RestoreClientsDialog row, which carries `deletedOn` as a Date
     * @returns {Promise<boolean>} whether it succeeded — RestoreClientsDialog drops the row only on true
     */
    async onRestoreDeletedClient(client) {
      // Read before the restore: once the client is back, usage already counts it.
      const licenseNeeded = client.status === CLIENT_STATUS.ACTIVE && this.getAvailableLicenses('client') < 1;
      try {
        await callFirebaseFunction('restoreClient', { clientId: client.id });
        clientLifecycleAnalytics.trackClientRestored({
          targetClientId: client.id,
          daysSinceDeleted: this.getDaysSinceDeleted(client.deletedOn),
          restoredByDeleter: client.deletedBy ? client.deletedBy === this.userDetails.userId : null,
          clientStatus: client.status,
        });
        this.notifySuccess(this.$t('clients.notifyClientRestored', { clientName: client.clientName }));
      } catch (err) {
        const isNotRestorableError = [
          'functions/not-found',
          'functions/failed-precondition',
        ].includes(err?.code);
        const { NOT_RESTORABLE, UNEXPECTED } = clientLifecycleAnalytics.RESTORE_FAILED_REASONS;
        clientLifecycleAnalytics.trackClientRestoreFailed({
          targetClientId: client.id,
          reason: isNotRestorableError ? NOT_RESTORABLE : UNEXPECTED,
        });
        this.notifyNegative(this.$t(isNotRestorableError
          ? 'clients.notifyClientNotRestorable'
          : 'common.errors.generic'));
        return false;
      }

      // Awaited so the Restore buttons stay disabled until usage is recounted.
      try {
        await this.updateLicenseUsage(licenseNeeded);
      } catch (err) {
        logEvent('Error updating license usage after restoring a client', {
          context: { context_data: { clientId: client.id, licenseNeeded, errorCode: err?.code ?? null } },
        });
        this.notifyNegative(this.$t('importPipeline.notifications.client.errorUpdatingSubscription'));
      }
      return true;
    }, // onRestoreDeletedClient()

    onViewAuditTrail(client) {
      this.openComponentDialog(ViewAuditTrailDialog, {
        collection: 'clients',
        resourceId: client.id,
      });
    }, // onViewAuditTrail()

    onCopySupportInfo(clientId) {
      copySupportInfoToClipboard({ 'target client': clientId });
    }, // onCopySupportInfo()

    async onManageUsers(clientId) {
      const editedClient = {
        id: clientId,
        ...this.clients.find((x) => x.id === clientId),
      };

      this.$q.loading.show();
      try {
        await this.openClientProfile(editedClient, {
          source: EDIT_CLIENT_PROFILE_SOURCES.CLIENTS,
          tabToOpen: EDIT_CLIENT_PROFILE_TABS.ACCESS,
        });
      } finally {
        this.$q.loading.hide();
      }
    }, // onManageUsers()

    async onGoToClient(client) {
      if (client.status === 'inactive') {
        await this.selectDisabledClient({ clientId: client.id });
      } else {
        await this.changeCurrentClientById(client.id);
      }

      this.$router.push('/');
    }, // onGoToClient()

    showImportClientsDialog() {
      this.openComponentDialog(
        ImportClientsDialog,
        { },
        async () => {
        },
      );
    }, // showImportClientsDialog()

    async loadUsers() {
      this.isLoadingUsers = true;

      this.usersLoadingPromise = this.bindUsers(this.userDetails.clinic);
      await this.usersLoadingPromise;

      this.isLoadingUsers = false;
    },

    showRefreshAppointments(client) {
      return this.connectedClinic.integration
        && POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration)
        && !!client.integrationReference;
    },

    isStatusSwitchDisabled(client) {
      return this.offlineMode || (client.integrationReference && this.autoImportClients);
    },

  },
};
</script>
